import {NgModule, inject, provideAppInitializer} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {MatNativeDateModule} from '@angular/material/core';
import {
  AuthHttpInterceptor,
  AuthModule,
  AuthService,
} from '@auth0/auth0-angular';
import {StoreModule} from '@ngrx/store';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {SelectedOrganizationInterceptor} from 'services/interceptors/selected-organization.interceptor';
import {environment} from '../environments/environment';
import {AppRoutingModule} from './app-routing.module';
import {TranslocoRootModule} from './core/transloco/transloco-root.module';
import {MainModule} from './layouts/main/main.module';
import {NotFoundModule} from './layouts/not-found/not-found.module';
import {LoadingInterceptor} from './services/loading/interceptor/loading-interceptor.service';
import {reducers} from './shared/store/state.reducers';
import {AppComponent} from './app.component';
import {DefaultModule} from './layouts/default/default.module';
import {StoreService} from './shared/store/store.service';
import {LoginService} from './services/login.service';
import {Observable} from 'rxjs';
import {timer, of} from 'rxjs';
import {switchMap, take} from 'rxjs';

export function initializeApp(
  authService: AuthService,
  storeService: StoreService,
  loginService: LoginService,
): () => Observable<boolean> {
  return () =>
    authService.isLoading$.pipe(
      switchMap((isLoading) => {
        if (isLoading) {
          return timer(100).pipe(
            switchMap(() =>
              initializeApp(authService, storeService, loginService)(),
            ),
          );
        }

        return authService.isAuthenticated$.pipe(
          take(1),
          switchMap((isAuthenticated) => {
            if (isAuthenticated) {
              storeService.refreshOrganizationStore();
              return timer(1000).pipe(switchMap(() => of(true)));
            }
            return of(true);

          }),
        );
      }),
      take(1),
    );
}

@NgModule({
  imports: [
    BrowserModule,
    AppRoutingModule,
    TranslocoRootModule,
    MainModule,
    DefaultModule,
    NotFoundModule,
    MatNativeDateModule,
    AuthModule.forRoot({
      domain: environment.auth0Domain,
      clientId: environment.auth0ClientId,
      cacheLocation: 'localstorage',
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: environment.auth0Audience,
        scope: 'openid profile email offline_access',
        max_age: 28800, // 8 hours session timeout
      },
      httpInterceptor: {
        allowedList: [
          {
            uri: `${environment.apiUrl}/*`,
            tokenOptions: {
              authorizationParams: {
                audience: environment.auth0Audience,
                scope: 'openid offline_access',
              },
            },
          },
        ],
      },
    }),
    StoreModule.forRoot(reducers, {}),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      connectInZone: true,
      autoPause: true,
      logOnly: false,
      // LogOnly: environment.production, // Restrict extension to log-only mode
    }),
  ],
  declarations: [AppComponent],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SelectedOrganizationInterceptor,
      multi: true,
    },
    {provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true},
    provideAppInitializer(() => {
      const initializerFunction = initializeApp(
        inject(AuthService),
        inject(StoreService),
        inject(LoginService),
      );
      return initializerFunction();
    }),
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
